import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  Avatar,
} from '@mui/material';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Flavor as IFlavor } from '../../../store/flavor/api/interface';
import { FormikProvider, useFormik } from 'formik';
import {
  createFlavor,
  getFlavor,
  patchFlavor,
} from '../../../store/flavor/api';
import { setStatus } from '../../../store/flavor/flavorSlice';
import { useEffect, useState } from 'react';
import { decryptString, getAssetURl } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit } from 'lodash';
import flavorSchema from '../../../validation/flavorSchema';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import DetailsForm from './DetailsForm';
import { fillFlavorState } from '../../../utils/fillFlavorData';
import {
  setStatus as selectFlavorSetStatus,
  reset as resetSelectFlavorReset,
} from '../../../store/selectFlavor/selectFlavorSlice';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';

const FlavorForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IFlavor; // Type Casting, then you can get the params passed via router

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.flavor);
  const { accessToken } = useAppSelector((state) => state.auth);
  const [response, setResponse] = useState<any>();
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const formik = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      code: '',
      subLabelEn: '',
      subLabelAr: '',
      descriptionEn: '',
      descriptionAr: '',
      previewUrl: '',
      position: '',
      pricePerKg: '',
      // isNew: false,
      activeState: true,
      imageItems: [
        {
          shapeId: '',
          // sliced: '',
          top: '',
          front: '',
        },
      ],
    },
    validationSchema: flavorSchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createFlavor(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          delete reqBody.country;
          delete reqBody.isBestSeller;
          delete reqBody.isTrending;
          delete reqBody.badges;
          delete reqBody.bestSellerScore;
          delete reqBody.trendingScore;
          delete reqBody.badgesUpdatedAt;
          await dispatch(patchFlavor(reqBody)).unwrap();
        }
        formik.resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectFlavorReset());
        dispatch(selectFlavorSetStatus({ status: 'idle' }));
        navigation('/flavor');
      } catch (error: any) {}
    },
  });

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        formik.setValues(fillFlavorState(state));
        setResponse(state);
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getFlavor({ id: decodeParam })
          ).unwrap();
          formik.setValues(fillFlavorState(record));
          setResponse(record);
        } catch (error: any) {
          navigation('/flavor');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, formik.setValues, state]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    field: string
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        documentType,
        accessToken
      );
      if (uploadResult?.data?.id) {
        formik.setFieldValue(field, uploadResult?.data?.keypath);
      }
    }
  };

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    label={'Label English'}
                    name='labelEn'
                    id='labelEn'
                    onChange={formik.handleChange}
                    value={formik.values.labelEn}
                    error={Boolean(
                      formik.errors.labelEn && formik.touched.labelEn
                    )}
                    helperText={
                      Boolean(
                        formik.errors.labelEn && formik.touched.labelEn
                      ) && formik.errors.labelEn
                    }
                  />
                  <TextField
                    fullWidth
                    label={'Label Arabic'}
                    name='labelAr'
                    id='labelAr'
                    onChange={formik.handleChange}
                    value={formik.values.labelAr}
                    error={Boolean(
                      formik.errors.labelAr && formik.touched.labelAr
                    )}
                    helperText={
                      Boolean(
                        formik.errors.labelAr && formik.touched.labelAr
                      ) && formik.errors.labelAr
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Sub Label English'}
                    name='subLabelEn'
                    id='subLabelEn'
                    onChange={formik.handleChange}
                    value={formik.values.subLabelEn}
                    error={Boolean(
                      formik.errors.subLabelEn && formik.touched.subLabelEn
                    )}
                    helperText={
                      Boolean(
                        formik.errors.subLabelEn && formik.touched.subLabelEn
                      ) && formik.errors.subLabelEn
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Sub Label Arabic'}
                    name='subLabelAr'
                    id='subLabelAr'
                    onChange={formik.handleChange}
                    value={formik.values.subLabelAr}
                    error={Boolean(
                      formik.errors.subLabelAr && formik.touched.subLabelAr
                    )}
                    helperText={
                      Boolean(
                        formik.errors.subLabelAr && formik.touched.subLabelAr
                      ) && formik.errors.subLabelAr
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Code'}
                    name='code'
                    id='code'
                    onChange={formik.handleChange}
                    value={formik.values.code}
                    error={Boolean(formik.errors.code && formik.touched.code)}
                    helperText={
                      Boolean(formik.errors.code && formik.touched.code) &&
                      formik.errors.code
                    }
                  />

                  {/* <TextField
                    fullWidth
                    label={'Preview URL'}
                    name='previewUrl'
                    id='previewUrl'
                    onChange={formik.handleChange}
                    value={formik.values.previewUrl}
                    error={Boolean(
                      formik.errors.previewUrl && formik.touched.previewUrl
                    )}
                    helperText={
                      Boolean(
                        formik.errors.previewUrl && formik.touched.previewUrl
                      ) && formik.errors.previewUrl
                    }
                  /> */}
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={formik?.values?.previewUrl ? 8.5 : 12}
                      sm={formik?.values?.previewUrl ? 8.5 : 12}
                    >
                      <TextField
                        type='file'
                        fullWidth
                        name='previewUrl'
                        id='previewUrl'
                        onChange={(e) =>
                          handleFileChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            DocumentType.Flavor,
                            'previewUrl'
                          )
                        }
                        inputProps={{ multiple: false, accept: 'image/*' }}
                        InputLabelProps={{ shrink: true }}
                        variant='outlined'
                        label={'Preview URL'}
                        error={Boolean(
                          formik.errors.previewUrl && formik.touched.previewUrl
                        )}
                        helperText={
                          Boolean(
                            formik.errors.previewUrl &&
                              formik.touched.previewUrl
                          ) && formik.errors.previewUrl
                        }
                      />
                    </Grid>
                    {formik?.values?.previewUrl && (
                      <Grid
                        item
                        xs={3.5}
                        sm={3.5}
                      >
                        <Avatar
                          imgProps={{ crossOrigin: 'anonymous' }}
                          src={getAssetURl(
                            formik?.values?.previewUrl,
                            assetUrl
                          )}
                          sx={{
                            borderRadius: 'unset',
                            m: 1,
                            objectFit: 'contain',
                            '& img': { objectFit: 'contain' },
                            width: { xs: '75px', sm: '100px' },
                            height: 50,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <TextField
                    fullWidth
                    label={'Position'}
                    name='position'
                    id='position'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.position}
                    error={Boolean(
                      formik.errors.position && formik.touched.position
                    )}
                    helperText={
                      Boolean(
                        formik.errors.position && formik.touched.position
                      ) && formik.errors?.position
                    }
                  />
                  {/* {formik?.values?.previewUrl && (
                      <Grid
                        item
                        xs={3.5}
                        sm={3.5}
                      >
                        <Avatar
                          imgProps={{ crossOrigin: 'anonymous' }}
                          src={getAssetURl(formik?.values?.previewUrl, assetUrl)}
                          sx={{
                            borderRadius: 'unset',
                            m: 1,
                            objectFit: 'contain',
                            '& img': { objectFit: 'contain' },
                            width: 100,
                            height: 50,
                          }}
                        />
                      </Grid>
                    )}
                  </Grid> */}

                  <TextField
                    fullWidth
                    label={'Price Per KG'}
                    name='pricePerKg'
                    id='pricePerKg'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.pricePerKg}
                    error={Boolean(
                      formik.errors.pricePerKg && formik.touched.pricePerKg
                    )}
                    helperText={
                      Boolean(
                        formik.errors.pricePerKg && formik.touched.pricePerKg
                      ) && formik.errors?.pricePerKg
                    }
                  />
                </Box>

                <Box sx={{ pt: 2.5 }}>
                  <TextField
                    fullWidth
                    label={'English Description'}
                    name='descriptionEn'
                    id='descriptionEn'
                    onChange={formik.handleChange}
                    value={formik.values.descriptionEn}
                    error={Boolean(
                      formik.errors.descriptionEn &&
                        formik.touched.descriptionEn
                    )}
                    helperText={
                      Boolean(
                        formik.errors.descriptionEn &&
                          formik.touched.descriptionEn
                      ) && formik.errors.descriptionEn
                    }
                  />
                </Box>

                <Box sx={{ pt: 2.5, pb: 2.5 }}>
                  <TextField
                    fullWidth
                    label={'Arabic Description'}
                    name='descriptionAr'
                    id='descriptionAr'
                    onChange={formik.handleChange}
                    value={formik.values.descriptionAr}
                    error={Boolean(
                      formik.errors.descriptionAr &&
                        formik.touched.descriptionAr
                    )}
                    helperText={
                      Boolean(
                        formik.errors.descriptionAr &&
                          formik.touched.descriptionAr
                      ) && formik.errors.descriptionAr
                    }
                  />
                </Box>

                <Grid
                  container
                  spacing={2}
                  py={2}
                >
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='activeState'
                          onChange={(e) =>
                            formik.setFieldValue(
                              'activeState',
                              e.target.checked
                            )
                          }
                          checked={formik.values.activeState}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'Active Status'}
                    />
                  </Grid>
                  {/* <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='isNew'
                          onChange={(e) =>
                            formik.setFieldValue('isNew', e.target.checked)
                          }
                          checked={formik.values.isNew}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'New'}
                    />
                  </Grid> */}
                </Grid>

                <DetailsForm
                  isAddMode={isAddMode}
                  response={response}
                  values={formik.values}
                  handleChange={formik.handleChange}
                  setFieldValue={formik.setFieldValue}
                  errors={formik.errors}
                  touched={formik.touched}
                  // onScrollFetchProductItem={onScrollFetchProductItem}
                  // isProductItemLoading={isProductItemLoading}
                  // uniqueProductData={uniqueProductData}
                  // searchProductItem={searchProductItem}
                />

                <Stack
                  alignItems='center'
                  sx={{ mt: 3 }}
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  <Button
                    variant='outlined'
                    onClick={() => navigation('/flavor')}
                  >
                    {'Cancel'}
                  </Button>

                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={formik.isSubmitting}
                  >
                    {isAddMode ? 'Add Flavor' : 'Edit Flavor'}
                  </LoadingButton>
                </Stack>
              </Card>
            </form>
          </FormikProvider>
        )}
      </Grid>
    </Grid>
  );
};

export default FlavorForm;
